import '@features/properties/components/tag-list';

import {
    Component,
    defineElement,
    FC,
    onDidLoad,
    useElement,
    useListen,
    useProp,
} from '@atomify/hooks';
import { Fragment, h } from '@atomify/jsx';
import { useStore } from '@atomify/kit';
import { Button } from '@atoms/button/button';
import { BPDFunctionalSvg } from '@atoms/svg/svg';
import { ExtendedAreaModels, rafThrottle, useUrlParams } from '@bpd-library/utilities';
import { propertiesStore } from '@features/properties/store';
import { addFilters, filterStore, getAllFilters, getSelectedFilters } from '@source/store/filters';
import { useTranslate } from '@source/utilities/hooks';
import { GTMPush } from '@utilities/gtm';
import { isMobile } from '@utilities/media-query';

import { getFilterOrder, PropertyFilterElement } from '../property-filter';
import { constructFilters, Filters } from './filters';

const HIDDEN_ACTIVE_COUNT_SELECTOR = '[js-hook-properties-filter-hidden-active-count]';
const FILTERS_ACTIVE_CLASS = 'properties-filters--filters-active';
const HAS_FILTER_EXPAND = 'properties-filters--has-expand';
const FILTERS_EXPANDED = 'properties-filters--expanded';
const FILTER_IS_HIDDEN = 'property-filter--is-hidden';
const FILTER_ACCORDION = 'property-filter--accordion';
const FILTER_DROPDOWN = 'property-filter--dropdown';
const EXPAND_TOGGLE_WIDTH_DESKTOP = 130;
const EXPAND_TOGGLE_WIDTH_MOBILE = 80;

type FilterVisibilityState = { left: number; width: number; overflow?: boolean };

interface PropertiesFiltersElement extends Component {
    expanded: boolean;
}

export const PropertiesFilters: FC<PropertiesFiltersElement> = ({ element }) => {
    const { replace, clearParam } = useUrlParams();
    const [, subscribe] = useStore(filterStore);

    // Setup filters
    const [data] = useProp<ExtendedAreaModels>('data');
    const [, setExpanded, watchExpanded] = useProp<boolean>('expanded', false);

    const filterDropdowns = useElement<HTMLDivElement>('[js-hook-properties-filters-dropdowns]');
    const expandToggleWrapper = useElement<HTMLDivElement>(
        '[js-hook-properties-filters-expand-toggle-wrapper]',
    );

    const filterItemsWidth = new Map<PropertyFilterElement, FilterVisibilityState>();
    let filterItems: undefined | PropertyFilterElement[];
    let isMobileDevice = isMobile();

    const updateItemsShownThrottled = rafThrottle(updateItemsShown);
    useListen(window, 'orientationchange', updateItemsShownThrottled);
    useListen(window, 'resize', updateItemsShownThrottled);

    onDidLoad(async () => {
        const allProjectsSold = propertiesStore.getState().allProjectsSold;
        const filters = await constructFilters(data, { allProjectsSold });
        const sortedFilters = sortFilters(filters);
        addFilters(sortedFilters);

        filterDropdowns.current?.append(
            <Fragment>
                {Object.keys(sortedFilters).map((key) => (
                    <property-filter
                        {...sortedFilters[key as keyof typeof filters]}
                        class={FILTER_DROPDOWN}
                        onToggle={collapseAllFilters}
                    />
                ))}
            </Fragment>,
        );

        filterItems = Array.from(
            element.querySelectorAll<PropertyFilterElement>('property-filter'),
        );

        // Wait until all filters are rendered then update the state of the <properties-filters>
        await Promise.all(filterItems?.map((item) => item && item.componentOnReady()));

        updateFilterItemsWidth();
        updateItemsShown();
        updateFilterActiveState();
    });

    subscribe(handleFilterChange, ['filters']);

    watchExpanded((expanded) => {
        collapseAllFilters();
        element.classList[expanded ? 'add' : 'remove'](FILTERS_EXPANDED);

        GTMPush({
            event: 'gtm.filters',
            eventCategory: 'Woonwensen',
            eventAction: expanded ? 'Expand filters' : 'Contract filters',
        });

        if (isMobileDevice) {
            if (expanded) {
                showAllFilters();
                convertTo('accordion');
            } else {
                convertTo('dropdown');
                updateFilterItemsWidth();
                updateItemsShown();
            }
        }
    });

    function sortFilters(filters: Filters) {
        const sortedFilters: Filters = {};
        getFilterOrder().forEach((filter) => {
            if (filters[filter]) {
                sortedFilters[filter] = filters[filter];
            }
        });
        return sortedFilters;
    }

    function showAllFilters() {
        filterItems?.forEach((filterItem) => {
            filterItem.classList.remove(FILTER_IS_HIDDEN);
        });
    }

    function convertTo(to: 'accordion' | 'dropdown') {
        const addName = to === 'accordion' ? FILTER_ACCORDION : FILTER_DROPDOWN;
        const removeName = to === 'accordion' ? FILTER_DROPDOWN : FILTER_ACCORDION;
        filterItems?.forEach((filterItem) => {
            filterItem.classList.add(addName);
            filterItem.classList.remove(removeName);
        });
    }

    function updateFilterItemsWidth() {
        filterItemsWidth.clear();

        // go through items twice to prevent layout thrashing

        showAllFilters();
        filterItems?.forEach((filterItem) => {
            const { left } = filterItem.getBoundingClientRect();

            filterItemsWidth.set(filterItem, {
                width: filterItem.offsetWidth,
                left: Math.round(left),
            });
        });
    }

    function updateItemsShown() {
        if (!(filterItems?.length && filterDropdowns.current)) return;

        const { left: wrapperLeft, width: wrapperWidth } =
            filterDropdowns.current.getBoundingClientRect();

        const newIsMobile = isMobile();

        if (isMobileDevice !== newIsMobile) {
            isMobileDevice = newIsMobile;

            if (element.expanded) {
                setExpanded(false);
                return;
            }
            updateFilterItemsWidth();
        }

        element.classList.remove(HAS_FILTER_EXPAND);

        const { width: lastFilterWidth, left: lastFilterLeft } = filterItemsWidth.get(
            filterItems[filterItems.length - 1],
        )!;

        const showToggle = lastFilterLeft + lastFilterWidth > wrapperLeft + wrapperWidth;
        const toggleWidth = showToggle
            ? isMobileDevice
                ? EXPAND_TOGGLE_WIDTH_MOBILE
                : EXPAND_TOGGLE_WIDTH_DESKTOP
            : 0;
        let hasOverflowItems = false;

        filterItemsWidth.forEach((item, filter) => {
            const { left: filterLeft, width: filterWidth } = item;

            const isOverflowing =
                filterLeft + filterWidth > wrapperLeft + wrapperWidth - toggleWidth;

            if (isOverflowing) {
                filter.classList.add(FILTER_IS_HIDDEN);
                element.classList.add(HAS_FILTER_EXPAND);
                item.overflow = true;
                hasOverflowItems = true;
            } else {
                filter.classList.remove(FILTER_IS_HIDDEN);
                item.overflow = false;
            }
        });

        if (hasOverflowItems) {
            renderExpandToggle();
            setTimeout(updateExpandToggleLabel, 50);
        } else {
            expandToggleWrapper.current!.innerHTML = '';
        }
    }

    function handleFilterChange() {
        const selectedFilters = getSelectedFilters();
        const allFilters = getAllFilters();

        Object.keys(allFilters).forEach(clearParam);

        if (selectedFilters.length) {
            element.classList.add(FILTERS_ACTIVE_CLASS);

            selectedFilters.forEach(({ minValue, filterName, maxValue, stringValues }) => {
                if (minValue && !maxValue) replace(filterName, `${minValue}`);
                if (maxValue && !minValue) replace(filterName, `${minValue}`);
                if (maxValue && minValue) replace(filterName, `${minValue}.${maxValue}`);
                if (stringValues) replace(filterName, stringValues.join('.'));
            });
        } else {
            element.classList.remove(FILTERS_ACTIVE_CLASS);
        }

        updateFilterActiveState();

        // TODO: improve hacky logic
        if (!(isMobileDevice && element.expanded)) {
            updateFilterItemsWidth();
            updateItemsShown();
        }
    }

    function updateFilterActiveState() {
        if (!filterItems) return;
        const activeFilters = filterItems.filter(
            (element) => element.querySelectorAll('input:checked').length > 0,
        );
        filterItems.forEach((filter) => filter.classList.remove('active'));
        activeFilters.forEach((filter) => filter.classList.add('active'));
    }

    function updateExpandToggleLabel() {
        if (!expandToggleWrapper.current) return;
        const countField = expandToggleWrapper.current.querySelector(HIDDEN_ACTIVE_COUNT_SELECTOR);
        if (!countField) return;

        const activeCount = countHiddenActiveFilters();
        countField.innerHTML = activeCount ? ` (${activeCount})` : '';
    }

    function countHiddenActiveFilters() {
        let count = 0;
        filterItemsWidth.forEach((item, filter) => {
            if (!item.overflow) return;
            count += filter.querySelectorAll('input:checked').length;
        });
        return count;
    }

    function collapseAllFilters() {
        if (!filterItems?.length) return;
        filterItems.forEach((filter) => filter.removeAttribute('expanded'));
    }

    function renderExpandToggle() {
        if (!expandToggleWrapper.current) return;
        const expandKey = isMobileDevice ? 'Filters.Filters' : 'Filters.AllFilters';

        expandToggleWrapper.current.innerHTML = '';
        expandToggleWrapper.current.append(
            <button class="properties-filters__expand-button" onClick={() => setExpanded(true)}>
                <BPDFunctionalSvg name="icons-filter" />
                <span class="properties-filters__expand-button-label">
                    {useTranslate(expandKey)}
                    <span js-hook-properties-filter-hidden-active-count />
                </span>
            </button>,
            <button class="properties-filters__collapse-button" onClick={() => setExpanded(false)}>
                <BPDFunctionalSvg name="icons-close" />
                <span class="properties-filters__collapse-button-label">
                    {useTranslate('Filters.AllFiltersClose')}
                </span>
            </button>,
        );
    }

    return (
        <Fragment>
            <div class="properties-filters__dropdowns-wrapper">
                <Button
                    variant="grey-5"
                    icon="icons-close"
                    size="small"
                    width="square"
                    shape="round"
                    classes="properties-filters__expanded-mobile-control properties-filters__collapse"
                    onClick={() => setExpanded(false)}
                />

                <span class="properties-filters__expanded-mobile-only u-h4 u-margin-top-2 u-margin-bottom-4">
                    {useTranslate('Filters.Filters')}
                </span>

                <div class="properties-filters__expanded-mobile-tag-list">
                    <bpd-tag-list disable-collapse />
                </div>

                <div class="properties-filters__dropdowns" js-hook-properties-filters-dropdowns />

                <div
                    class="properties-filters__expand-toggle-wrapper"
                    js-hook-properties-filters-expand-toggle-wrapper
                />

                <Button
                    variant="primary"
                    width="full-width"
                    classes="properties-filters__expanded-mobile-control properties-filters__confirm"
                    label={useTranslate('ShowResults')}
                    onClick={() => setExpanded(false)}
                />
            </div>

            <div class="properties-filters__action-bar">
                <div class="properties-filters__tag-list">
                    <bpd-tag-list />
                </div>
            </div>
        </Fragment>
    );
};

PropertiesFilters.props = {
    data: {
        type: Object,
        required: true,
    },
    expanded: {
        type: Boolean,
        reflectToAttr: true,
    },
};

defineElement('properties-filters', PropertiesFilters);
